import React, { useState, useEffect, useRef } from 'react';
import { 
  Box, 
  Typography, 
  TextField, 
  Button, 
  Alert, 
  CircularProgress,
  Paper,
  Link,
  Divider,
  Chip
} from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { getUserProfile, updateUserProfile, syncSteamLibrary, updateStatusCounts, checkSteamSyncStatus } from '../../services/api';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import AccessTimeIcon       from '@mui/icons-material/AccessTime';
import CheckCircleOutlineOutlinedIcon from '@mui/icons-material/CheckCircleOutlineOutlined';
import ErrorOutlineIcon     from '@mui/icons-material/ErrorOutline';
import Dialog from '../dialogs/Dialog';

const SyncStatusDisplay: React.FC<{
  status: number | null;
  message: string | null;
  lastSyncTime: string | null;
  nextSyncTime: string | null;
}> = ({ status, message, lastSyncTime, nextSyncTime }) => {
  if (status === null) return null;

  let icon: React.ReactNode;
  let label: string;
  let timeLabel: string | null = null;
  
  switch (status) {
    case 0:
      icon = <HourglassEmptyIcon color="disabled" />;
      label = '待機中';
      timeLabel = lastSyncTime ? `前回の同期: ${lastSyncTime} JST` : null;
      break;
    case 1:
      icon = <AccessTimeIcon color="info" />;
      label = 'キュー待ち';
      timeLabel = lastSyncTime ? `前回の同期: ${lastSyncTime} JST` : null;
      break;
    case 2:
      icon = <CircularProgress size={20} />;
      label = '処理中';
      timeLabel = lastSyncTime ? `前回の同期: ${lastSyncTime} JST` : null;
      break;
    case 3:
      icon = <CheckCircleOutlineOutlinedIcon color="success" />;
      label = '完了';
      timeLabel = lastSyncTime ? `同期完了: ${lastSyncTime} JST` : null;
      break;
    case 4:
      icon = <ErrorOutlineIcon color="error" />;
      label = '失敗';
      // エラー時は30分後に再試行可能なことを表示
      if (lastSyncTime) {
        const syncDate = new Date(lastSyncTime.replace(' JST', ''));
        const retryDate = new Date(syncDate.getTime() + 1 * 9000); // 9秒後
        const now = new Date();
        
        if (now >= retryDate) {
          timeLabel = '再試行可能です';
        } else {
          const seconds = Math.ceil((retryDate.getTime() - now.getTime()) / 1000);
          timeLabel = `再試行まで約${seconds}秒`;
        }
      }
      break;
    default:
      icon = null;
      label = '';
  }

  return (
    <Paper 
      variant="outlined" 
      sx={{ 
        p: 2, 
        mb: 2,
        bgcolor: status === 4 ? 'rgba(211, 47, 47, 0.04)' : 'inherit' // エラー時は薄い赤背景
      }}
    >
      <Box display="flex" alignItems="center" justifyContent="space-between" gap={1}>
        <Box display="flex" alignItems="center" gap={1}>
          {icon}
          <Typography variant="body2" fontWeight="500" whiteSpace="nowrap">
            {label}
          </Typography>
        </Box>
        {message && (
          <Typography variant="caption" color="text.secondary">
            {message}
          </Typography>
        )}
      </Box>
      {timeLabel && (
        <Box mt={1}>
          <Typography variant="caption" color="text.secondary">
            {timeLabel}
          </Typography>
        </Box>
      )}
    </Paper>
  );
};

const SteamSettings: React.FC = () => {
  const [steamId, setSteamId] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [syncLoading, setSyncLoading] = useState<boolean>(false);
  const [success, setSuccess] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [hasSteamId, setHasSteamId] = useState<boolean>(false);
  const [updateLoading, setUpdateLoading] = useState<boolean>(false);
  const [lastUpdateTime, setLastUpdateTime] = useState<number>(0);
  const [nextSyncTime, setNextSyncTime] = useState<string | null>(null);
  const [lastSyncTime, setLastSyncTime] = useState<string | null>(null);
  const [canSync, setCanSync] = useState<boolean>(true);
  const [syncStatus, setSyncStatus] = useState<number | null>(null);
  const [syncStatusMessage, setSyncStatusMessage] = useState<string | null>(null);
  const [showPrivacyDialog, setShowPrivacyDialog] = useState<boolean>(false);
  const pollingRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        setLoading(true);
        const response = await getUserProfile();
        if (response.data.steam_id) {
          setSteamId(response.data.steam_id);
          setHasSteamId(true);
          // プロフィール取得成功後に同期ステータスをチェック
          checkSyncStatus();
        }
      } catch (err) {
        setError('プロフィール情報の取得に失敗しました');
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
    
    // コンポーネントのアンマウント時にポーリングをクリア
    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
      }
    };
  }, []);

  // 同期ステータスを確認する関数
  const checkSyncStatus = async () => {
    try {
      const result = await checkSteamSyncStatus();
      setSyncStatus(result.status);
      setSyncStatusMessage(result.message);
      
      if (result.next_sync_time) {
        setNextSyncTime(result.next_sync_time);
      }
      
      if (result.last_sync_at) {
        const dt = new Date(result.last_sync_at);
        const formattedLast = dt.toLocaleString('ja-JP', {
          year: 'numeric', month: '2-digit', day: '2-digit',
          hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false
        }).replace(/\//g, '-').replace(/,/g, '');
        setLastSyncTime(formattedLast);
      }
      
      // ステータスに基づいて同期ボタンの状態を更新
      setCanSync(result.can_sync && result.status !== 2); // 処理中でなく、同期可能であれば
      
      // 処理中(2)の場合、定期的にステータスを確認
      if (result.status === 2 && !pollingRef.current) {
        pollingRef.current = setInterval(checkSyncStatus, 10000); // 10秒ごとに確認
      } 
      // 処理中でなくなったらポーリングを停止
      else if (result.status !== 2 && pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
      
    } catch (err) {
      console.error('同期ステータスの確認に失敗:', err);
    }
  };

  const handleSteamAuth = () => {
    const redirectUri = encodeURIComponent(`${window.location.origin}/auth/steam/callback?mode=link`);
    window.location.href = `${process.env.REACT_APP_API_URL || ''}/auth/steam/login/?redirect_uri=${redirectUri}`;
  };

  const handleSyncLibrary = async () => {
    setShowPrivacyDialog(true);
  };

  const handleConfirmSync = async () => {
    setShowPrivacyDialog(false);
    try {
      setSyncLoading(true);
      setError(null);
      setSuccess(null);
      
      const result = await syncSteamLibrary();
      setSuccess(result.message || 'Steamライブラリの同期を開始しました');
      
      // エラー状態の場合は同期可能フラグを true に設定
      setCanSync(result.status === 4);
      setSyncStatus(result.status);
      
      if (result.next_sync_time) {
        setNextSyncTime(result.next_sync_time);
      }
      
      // ポーリングを開始
      if (result.status === 2 && !pollingRef.current) {
        pollingRef.current = setInterval(checkSyncStatus, 10000);
      }
    } catch (err: any) {
      if (err.response?.status === 429) {
        setNextSyncTime(err.response.data.next_sync_time);
        setError(err.response.data.message);
      } else {
        setError(err.message || 'Steamライブラリの同期に失敗しました');
        // エラー時は同期可能フラグを true に設定
        setCanSync(true);
      }
    } finally {
      setSyncLoading(false);
    }
  };

  const handleUpdateStatusCounts = async () => {
    const now = Date.now();
    if (now - lastUpdateTime < 2000) {
      return;
    }

    try {
      setUpdateLoading(true);
      setError(null);
      setSuccess(null);
      setLastUpdateTime(now);
      
      const [result] = await Promise.all([
        updateStatusCounts(),
        new Promise(resolve => setTimeout(resolve, 2000))
      ]);
      
      setSuccess('ステータスカウントが更新されました');
    } catch (err: any) {
      setError(err.message || 'ステータスカウントの更新に失敗しました');
    } finally {
      setUpdateLoading(false);
    }
  };

  const handleUnlinkSteam = async () => {
    if (!window.confirm('Steamアカウントの連携を解除してもよろしいですか？')) {
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setSuccess(null);
      
      await updateUserProfile({ steam_id: null });
      setSteamId('');
      setHasSteamId(false);
      setSuccess('Steamアカウントの連携を解除しました');
      
      // ポーリングを停止
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
    } catch (err: any) {
      setError(err.message || 'Steamアカウントの連携解除に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  const privacyDialogContent = (
    <Box>
      <Typography variant="body1" gutterBottom>
        同期を開始する前に、以下のSteamプライバシー（公開設定）を確認してください。
        <Link
          href="https://steamcommunity.com/my/edit/settings"
          target="_blank"
          rel="noopener noreferrer"
          sx={{ 
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.5,
            color: 'primary.main',
            textDecoration: 'none',
            '&:hover': {
              textDecoration: 'underline',
            }
          }}
        >
          設定ページへ
          <OpenInNewIcon fontSize="small" />
        </Link>
      </Typography>
      <Box component="ul" sx={{ pl: 2 }}>
        <Typography component="li" variant="body2" color="text.secondary" gutterBottom>
          "ゲームの詳細"が「公開」に設定されているか
        </Typography>
        <Typography component="li" variant="body2" color="text.secondary" gutterBottom>
          "～総プレイ時間は常に非公開にする"のチェックが外されているか
        </Typography>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 2, mb: 2 }}>
        上記の要件が満たされていない場合、同期が失敗するか、あるいはPULSEライブラリでのゲームステータスが適切に設定されません。
      </Typography>
      <Alert 
        severity="info" 
        sx={{ 
          mb: 2,
          '& .MuiAlert-message': {
            fontSize: '0.8rem',
            lineHeight: '1'
          }
        }}
      >
        <Typography variant="caption">
          Steam Syncは現在ベータ版です。
          {/* 今後のアップデートでは、ユーザーが自身で発行したSteam APIキーを使用することで、
          プライバシー設定を有効にしたままでPULSEライブラリとの同期が可能となるオプションを実装予定です。
          また、このオプションを使用することで、PULSEライブラリに一括で最新プレイ日時を反映できるようになります。 */}
        </Typography>
      </Alert>
      <Box sx={{ mt: 2, display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
        <Button onClick={() => setShowPrivacyDialog(false)} color="inherit" size="small">
          キャンセル
        </Button>
        <Button onClick={handleConfirmSync} variant="contained" color="primary" size="small">
          同期を開始
        </Button>
      </Box>
    </Box>
  );

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Steam連携設定
      </Typography>

      <Paper elevation={0} sx={{ mb: 3, bgcolor: "background.paper" }}>
        <Typography variant="body2" color="text.secondary" paragraph>
          Steamアカウントを連携すると、Steamで所持しているゲームをPULSEライブラリと同期できます。また、PULSEへのログインにSteam認証を利用できるようになり、認証方法の紛失など万が一の際にもアカウントを保護することができます。
        </Typography>

        <Box sx={{ mb: 3 }}>
          {hasSteamId ? (
            <>
              <Alert
                severity="success"
                sx={{
                  mb: 2,
                  maxWidth: 500,
                  background:
                    "linear-gradient(135deg, rgba(23, 26, 33, 0.95), rgba(42, 71, 94, 0.95))",
                  color: "#ffffff",
                  border: "1px solid rgba(102, 192, 244, 0.2)",
                  borderRadius: "8px",
                  backdropFilter: "blur(8px)",
                  "& .MuiAlert-icon": {
                    color: "#66c0f4",
                  },
                }}
              >
                Steamアカウントが連携されています。Steam ID: {steamId}
              </Alert>
              <Box sx={{ display: 'flex', gap: 0, justifyContent: 'flex-end', mt: 0 }}>
                <Typography
                  component="button"
                  variant="caption"
                  onClick={handleSteamAuth}
                  disabled={loading}
                  sx={{
                    background: 'none',
                    border: 'none',
                    color: 'text.secondary',
                    cursor: loading ? 'default' : 'pointer',
                    textDecoration: 'underline',
                    fontSize: '0.7rem',
                    opacity: loading ? 0.5 : 1,
                    '&:hover': {
                      color: loading ? 'text.secondary' : 'primary.main',
                    },
                  }}
                >
                  再連携
                </Typography>
                <Typography
                  component="button"
                  variant="caption"
                  onClick={handleUnlinkSteam}
                  disabled={loading}
                  sx={{
                    background: 'none',
                    border: 'none',
                    color: 'text.secondary',
                    cursor: loading ? 'default' : 'pointer',
                    textDecoration: 'underline',
                    fontSize: '0.7rem',
                    opacity: loading ? 0.5 : 1,
                    '&:hover': {
                      color: loading ? 'text.secondary' : 'error.main',
                    },
                  }}
                >
                  連携を解除
                </Typography>
              </Box>
            </>
          ) : (
            <>
              <Button
                variant="contained"
                onClick={handleSteamAuth}
                disabled={loading}
                size="small"
                startIcon={
                  <img 
                    src="/icons/service/source/steam_.svg" 
                    alt="Steam" 
                    style={{ width: '20px', height: '20px', filter: 'invert(1)' }}
                  />
                }
                sx={{ 
                  mb: 2,
                  background: 'linear-gradient(135deg, rgba(23, 26, 33, 0.95), rgba(42, 71, 94, 0.95))',
                  color: '#ffffff',
                  border: '1px solid rgba(102, 192, 244, 0.2)',
                  backdropFilter: 'blur(8px)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, rgba(23, 26, 33, 1), rgba(42, 71, 94, 1))',
                    border: '1px solid rgba(102, 192, 244, 0.3)',
                  },
                  '&:disabled': {
                    background: 'linear-gradient(135deg, rgba(23, 26, 33, 0.3), rgba(42, 71, 94, 0.3))',
                    color: 'rgba(255, 255, 255, 0.3)',
                  }
                }}
              >
                Steamで認証する
              </Button>
            </>
          )}
        </Box>

        <Divider sx={{ my: 3 }} />

        <Box>
          <Box display="flex" alignItems="center">
            <Typography variant="subtitle1">
              Steam Sync 
            </Typography>
            <Chip 
              label="BETA v0.1.2" 
              size="small" 
              color="default" 
              variant="outlined" 
              sx={{ 
                ml: 1, 
                height: 18, 
                borderColor: 'rgba(102, 192, 244, 0.5)',
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                borderRadius: '4px',
                fontSize: '11px',
                color: 'rgba(102, 192, 244, 1)',
              }} 
            />
          </Box>
          <Typography variant="body2" paragraph color="text.secondary">
            SteamのゲームをPULSEライブラリと同期します。
            プレイ状況に応じて自動でステータスが振り分けられますが、正確なステータスは手動で設定してください。
            なお、本機能（現在のバージョン）において既存のゲームステータスが更新・削除されることはありません。
          </Typography>

          <SyncStatusDisplay
            status={syncStatus}
            message={syncStatusMessage}
            lastSyncTime={lastSyncTime}
            nextSyncTime={nextSyncTime}
          />

          {(success && success.includes('同期')) && (
            <Alert severity="info" sx={{ mb: 2 }}>
              {success}
            </Alert>
          )}

          {(error && error.includes('同期')) && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Button
            variant="outlined"
            color="primary"
            onClick={handleSyncLibrary}
            disabled={syncLoading || !hasSteamId || !canSync}
            startIcon={syncLoading ? <CircularProgress size={20} /> : null}
            sx={{ mt: 1 }}
            size="small"
          >
            {canSync ? 'ライブラリを同期' : syncStatus === 2 ? '同期中...' : 'ライブラリを同期'}
          </Button>
        </Box>

        <Divider sx={{ my: 3 }} />

        <Box>
          <Typography variant="subtitle1" gutterBottom>
            ステータスカウント修復ツール
          </Typography>

          <Typography variant="body2" paragraph color="text.secondary">
            ライブラリのステータスカウントが正しく集計されていない場合、手動で修復できます。
          </Typography>

          {(success && !success.includes('同期')) && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {success}
            </Alert>
          )}

          {(error && !error.includes('同期')) && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Button
            variant="outlined"
            color="secondary"
            onClick={handleUpdateStatusCounts}
            disabled={updateLoading || Date.now() - lastUpdateTime < 2000}
            startIcon={updateLoading ? <CircularProgress size={20} /> : null}
            sx={{
              mt: 1,
              color: "text.secondary",
              borderColor: "divider",
              "&:hover": {
                backgroundColor: "action.hover",
                borderColor: "text.secondary",
              },
            }}
            size="small"
          >
            {updateLoading ? "更新中..." : "ライブラリを修復"}
          </Button>
        </Box>
      </Paper>

      <Dialog
        open={showPrivacyDialog}
        onClose={() => setShowPrivacyDialog(false)}
        title="Steamプライバシー設定の確認"
        content={privacyDialogContent}
        showCloseButton={false}
        width={480}
      />
    </Box>
  );
};

export default SteamSettings; 